/**
 * The one answer to "a logged-out visitor engaged with the Plan-with-AI box".
 *
 * ## Why this exists
 *
 * `HeroSearchCard` was always shared — the homepage and both /discover guide pages render the
 * same component. What was not shared was the decision this file now owns. `18b57713a` built
 * the guide pages passing their own `onSubmit`, which did `history.push("/register")`, because
 * that is what every caller did then. `9a0a255b9` then fixed the homepage to prompt in place,
 * but put that inside `PlanTabContent` — the homepage-only branch of the card. It touched four
 * files and none of them were discover.
 *
 * So the fix landed on one variant rather than on the card's contract, and `onSubmit` was the
 * escape hatch that let two owners of one decision drift apart: the homepage prompted in place
 * while the guide pages still threw the reader onto a signup form. That is the difference Luuk
 * hit (#stippl-powerteam, 2026-09-02). Putting the behaviour here, with both callers using it,
 * is what stops it happening a third time.
 *
 * ## Mobile web goes to the store, everywhere
 *
 * 123yb691bdn: "On mobile web all plan CTA's should direct to appstore/playstore". Every
 * surface that renders this box passes `mobile: "store"`, so a phone never sees the signup
 * form from a plan CTA.
 *
 * This supersedes the homepage's own behaviour from `9a0a255b9`, which kept the modal on a
 * phone with the store row underneath so a visitor could still make a web account. That was
 * the right call in isolation; the ticket is the later one, and it is about consistency across
 * every plan CTA rather than about the homepage specifically.
 *
 * `mobile` stays a parameter rather than becoming a constant on purpose. It is the seam the
 * two behaviours drifted apart along, and keeping it named means a future surface has to say
 * which it wants instead of inheriting one by accident. Both callers say `"store"` today.
 *
 * ## Two traps, both already paid for elsewhere
 *
 * **The snackbar.** `RegisterBox` calls `useSnackbar()`, and only `App.tsx` provides that
 * context. These routes are mounted by `marketingRoutes.tsx` — the marketing entry, which
 * mounts Apollo, the router and BusinessContext and nothing else. Opening the modal without
 * the provider is what took the homepage down with "Cannot read properties of null (reading
 * 'openSnackbar')". Navigating to /register was never a design choice here so much as the
 * thing that happened to avoid this. Both the provider and the modal stay lazy, so Firebase
 * auth and react-simple-snackbar travel in the chunk that needs them rather than sitting on
 * the render path of every SSR'd guide page.
 *
 * **Hydration.** These pages are server-rendered. `isMobileOnly` reads `navigator.userAgent`,
 * which does not exist on the server, so the check lives in an effect and starts `false` —
 * the server render and the first client render agree, and the device answer arrives after
 * mount. Same shape as `useAppDownloadUrl`, and for the same reason.
 *
 * The store URL is the attributed AppsFlyer OneLink via `useAppDownloadUrl`, never a raw
 * store link: an install from here should not land in Organic.
 *
 * ## The click that looked dead
 *
 * 123yb692az8: "Doesnt open modal" on /discover/japan/hiroshima. It does open — `fallback={null}`
 * below just meant the page held its previous frame, with zero visible change, for however long
 * the three chunks under `<PlanGateModal>` took to fetch and evaluate (LoginModal, the snackbar
 * provider, and `ReduxProvider` pulling in the whole store and Firebase auth underneath it). The
 * Jam recording shows "Open the map" clicked 16 times in 9 seconds; reproducing it, two CDP
 * `evaluate` calls timed out at 45s before the modal appeared. Console had no errors, because
 * there was nothing wrong — just nothing to look at.
 *
 * `PlanGateLoading` below replaces the null fallback with a dimmed backdrop and spinner, so a
 * click always produces something. `dcbd28f47` landed this alongside a second, independent fix —
 * `prefetchPlanGate`, an idle-time prefetch of the same three chunks — and `f214ff646` reverted
 * both wholesale six hours later when every plan CTA broke in production (`123yb692k2p`, the
 * snackbar trap above). The revert never proved which change was to blame, but named the
 * prefetch the more suspicious of the two: it changes when React sees those modules settle, and
 * this is a context that has broken on timing before. `PlanGateLoading` carries no such risk — it
 * only changes what renders while the same imports happen the same way they always did — so it
 * comes back here on its own. The prefetch does not; if it comes back, it comes back separately
 * and with its own answer for what actually broke, not bundled with an unrelated visible fix.
 *
 * ## When the trap catches anyway
 *
 * Both traps above are things this file already knows to route around, and they have failed
 * anyway — twice, in two different shapes:
 *
 * - `123yb692k2p` (2026-09-08): `TypeError: Cannot read properties of null (reading
 *   'openSnackbar') at useSnackbar` — every plan CTA on the marketing site dead for six hours on
 *   launch day.
 * - `123yb692mta` (2026-09-09): `TypeError: Cannot read properties of null (reading 'store') at
 *   useSelector` — reproduced on production *and* on a current-main preview, by clicking "Plan
 *   with AI" on /discover/thailand.
 *
 * Neither trigger pinned down deterministically: a scripted click opens the modal fine in about
 * a second, "Login here" doesn't crash on its own, yet real click sequences produced two white
 * screens and one click that did nothing for five seconds. What both incidents share isn't the
 * cause, it's the failure shape — a provider mounts, a descendant reads null context — and
 * nothing in the marketing tree has ever caught a render error there (`MarketingShell.tsx` says
 * so explicitly: "No top-level Suspense here"). So the throw unmounts the *whole page*. A visitor
 * gets a white screen: no error, no navigation, nothing to retry.
 *
 * `PlanGateErrorBoundary` exists because this exact failure shape has now happened twice with two
 * different contexts, and chasing a third deterministic trigger is not what stops a fourth — an
 * error boundary contains a crash by construction, whichever context breaks next. See its own
 * comment below for what it does when it catches one.
 */
import { useAppDownloadUrl } from "components/common/useAppStoreLink";
import trackEvent from "components/tracking/trackEvent";
import trackFathom from "components/tracking/trackFathom";
import React, { useCallback, useEffect, useLayoutEffect, useRef, useState } from "react";
import { OsTypes, isMobileOnly, osName } from "react-device-detect";
import navigateToApp from "./navigateToApp";

// Named rather than inlined into `React.lazy` so `importReduxProvider` below reads the same way.
const importLoginModal = () => import("components/auth/LoginModal");
const importSnackbarProvider = () => import("react-simple-snackbar");

const LoginModal = React.lazy(importLoginModal);
const SnackbarProvider = React.lazy(importSnackbarProvider);

/**
 * The modal needs Redux too, and finding that out took a crash.
 *
 * `RegisterBox` needs `useSnackbar()`, which the note above is about. Switching to the login
 * view mounts `LoginBox`, and that reads `useSelector((state) => state)` — so the modal opened
 * fine and "Login here" took the page down with "Cannot read properties of null (reading
 * 'store')". One view was covered and the other was not: the same mistake, in the same file,
 * twice. Both views are covered now rather than the one I happened to open first.
 *
 * Lazy like the others, so the store and its middleware stay off the render path of a
 * marketing page that never opens the modal.
 */
const importReduxProvider = async () => {
	const [{ Provider }, store] = await Promise.all([import("react-redux"), import("store")]);
	const Wrapped = ({ children }: { children?: React.ReactNode }) => <Provider store={store.default}>{children}</Provider>;
	return { default: Wrapped };
};
const ReduxProvider = React.lazy(importReduxProvider);

export interface PlanGate {
	authOpen: boolean;
	setAuthOpen: (open: boolean) => void;
	/** Call on click. Sends a phone to the store and opens the modal everywhere else. */
	requestPlan: () => void;
	/** True once the mount effect has run on a phone. Server and first client render say false. */
	isPhone: boolean;
	/**
	 * Where a phone is sent. Exposed so the decision this hook makes is assertable: jsdom's
	 * `window.location` can be neither redefined nor spied on, so a test cannot observe the
	 * navigation itself — but it can check that the URL chosen is the attributed OneLink.
	 */
	storeUrl: string;
}

export type KnownPlanGatePath =
	| "home_plan_ai"
	| "discover_country"
	| "discover_destination"
	| "discover_stay_search"
	// The country hub's route map: saving a ready-made route from the Routes tab, saving from the My route builder, and the panel's
	// "Plan {country} in Stippl" / "Start from scratch" CTA (nothing to save: a signup, then the app's home).
	| "countrymap_route"
	| "countrymap_myroute"
	| "countrymap_scratch";

// Widened beyond the known callers above: feature pages and nav/footer CTAs track
// `path` values built at the call site (route paths via `match.path`, `topbar: ${match.path}`,
// `footer: ${match.path}`, …) rather than from a fixed vocabulary. `(string & {})` keeps the
// literals autocompleting while still accepting any other string — see
// https://www.totaltypescript.com/tips/create-autocomplete-helper-which-allows-for-arbitrary-values.
export type PlanGatePath = KnownPlanGatePath | (string & {});

export function usePlanGate(path: PlanGatePath, mobile: "modal" | "store" = "modal"): PlanGate {
	const [authOpen, setAuthOpen] = useState(false);
	const [isPhone, setIsPhone] = useState(false);
	// react-modal's mobile body-lock (position: fixed, see General.scss `.ReactModal__Body--open`)
	// pins `top` to `--scroll-lock-offset` (set below), rather than the scroll offset the page was
	// actually at, so without this, opening the modal snaps the page to the top on a phone.
	// `discover_stay_search` (TravelWidget's search button) is the one `usePlanGate` caller that
	// can open this modal on mobile — every other caller passes `mobile: "store"` and never reaches
	// it there — so 123yb692n1u only ever showed up on that CTA. Same fix CommunitySection.tsx
	// already applies to its own modal: capture the offset before locking, restore it once the lock
	// lifts. The CSS variable is set synchronously here, in the click handler, rather than in a
	// `useEffect`/`useLayoutEffect` keyed on `authOpen` — react-modal's own body-lock effect lives
	// deeper in the tree (inside `PlanGateModal` -> `LoginModal` -> `Modal`) and React commits child
	// layout effects before ancestor ones, so a layout effect up here would still lose the race and
	// let the class apply with the default (unset) offset for one frame.
	const scrollYBeforeOpenRef = useRef(0);
	const pathnameBeforeOpenRef = useRef("");

	useEffect(() => {
		setIsPhone(isMobileOnly);
	}, []);

	useEffect(() => {
		if (authOpen) return undefined;
		const restoreY = scrollYBeforeOpenRef.current;
		const pathAtOpen = pathnameBeforeOpenRef.current;
		if (!restoreY || typeof window === "undefined") return undefined;
		const frame = requestAnimationFrame(() => {
			if (window.location.pathname === pathAtOpen) window.scrollTo(0, restoreY);
			document.documentElement.style.removeProperty("--scroll-lock-offset");
		});
		return () => cancelAnimationFrame(frame);
	}, [authOpen]);

	// 123yb6937h3: always the attributed OneLink now, mobile or desktop -- see the hook's doc
	// comment in useAppStoreLink.ts.
	const storeUrl = useAppDownloadUrl();

	const requestPlan = useCallback(() => {
		if (isPhone && mobile === "store") {
			// The store-CTA events the rest of the site already fires, not a new name and not
			// `click_signup_cta` — this click never reaches a signup form, so counting it as one
			// would overstate that funnel. Event names are a cross-platform contract
			// (operations/analytics.md); `path` is what separates this from the other store CTAs.
			trackEvent(osName === OsTypes.Android ? "click_googleplay_cta" : "click_appstore_cta", { path, placement: "plan_gate" }, "discover");
			// The Fathom goals every store CTA on the marketing site fires (`handleTrack` on the
			// feature pages, the old `handleSignupTracking` on the homepage sections). The gate
			// replaced those handlers on 2026-09-05 (123yb691qz1), so it owns the goals now.
			trackFathom(osName === OsTypes.Android ? "BNQKH0MH" : "FD21GVOP");
			window.location.href = storeUrl;
			return;
		}
		if (typeof window !== "undefined") {
			scrollYBeforeOpenRef.current = window.scrollY;
			pathnameBeforeOpenRef.current = window.location.pathname;
			document.documentElement.style.setProperty("--scroll-lock-offset", `${window.scrollY}px`);
		}
		// Reusing `click_signup_cta` rather than coining a name keeps one signup funnel; `path`
		// is what separates this from the eight other signup CTAs, per operations/analytics.md.
		trackEvent("click_signup_cta", { path }, "marketing_site");
		trackFathom("E4NT4HWN");
		setAuthOpen(true);
	}, [isPhone, mobile, storeUrl, path]);

	return { authOpen, setAuthOpen, requestPlan, isPhone, storeUrl };
}

/**
 * Covers the gap while `ReduxProvider` / `SnackbarProvider` / `LoginModal` are still loading —
 * 123yb692az8, see the header comment. Before that fix there was nothing here at all, which is
 * what made the click look dead: nothing else on the page changes when `authOpen` flips true, so
 * holding the previous frame gave no sign the click had registered.
 *
 * NOT a `Suspense` `fallback` prop. `PlanGateModal` mounts this itself, gated on two things:
 * whether the chunks have taken longer than `PLANGATE_LOADING_APPEAR_DELAY_MS` to resolve (if
 * they haven't, this never mounts at all — a click that resolves fast enough has nothing to
 * flicker, because nothing was ever painted) and `shown` (the fade-in once it has; it never fades out — `PlanGateModalMounted` swaps it for the modal's own identical backdrop in one commit). A plain
 * `fallback` prop can't do either: React swaps fallback for children the instant the promise
 * resolves, in the same commit, so there's no way to delay showing it or animate it away — see
 * the entries in "When the trap catches anyway" below for what only-ever-instant swaps cost this
 * file once already.
 *
 * Styled off `.simple-modal`'s own after-open state (`css/_authModal.scss` — same backdrop
 * colour, same z-index) rather than a new look, since the modal this covers renders behind the
 * same overlay. Values are copied, not imported: this file's job is to keep Firebase and the
 * store off the render path of every page that calls `usePlanGate`, and `_authModal.scss` is
 * reached today only via components that may still be loading when this is visible.
 *
 * Draws its own spinner rather than reusing `components/common/Spinner.tsx`: that component's
 * `.spinner`/`@keyframes load8` rules live in `App.scss`, which the marketing bundle backing
 * these pages never loads (the exact gap `_authModal.scss`'s own header describes for
 * `.auth-modal`). Reaching for it here would have reproduced that bug one file over — an
 * invisible spinner instead of an invisible modal.
 */
const PLANGATE_LOADING_APPEAR_DELAY_MS = 150;

/**
 * Rendered as a sibling of `LoginModal` inside the same Suspense boundary, so it commits in
 * exactly the commit the modal does — never before the chunks are in, never a render later.
 * `useLayoutEffect` state updates flush synchronously before the browser paints, and react-modal
 * 3.x creates its portal in that same commit (overlay node in render, `open()` in
 * `componentDidMount`). So the first frame the browser paints already has the modal's overlay
 * and no `PlanGateLoading` — the two backdrops never overlap, and the screen is never without one.
 */
function PlanGateModalMounted({ onMount }: { onMount: () => void }) {
	useLayoutEffect(() => {
		onMount();
	}, [onMount]);
	return null;
}

function PlanGateLoading({ shown }: { shown: boolean }) {
	return (
		<div
			style={{
				position: "fixed",
				top: 0,
				left: 0,
				right: 0,
				bottom: 0,
				background: "rgba(0, 0, 0, 0.7)",
				zIndex: 9999,
				display: "flex",
				alignItems: "center",
				justifyContent: "center",
				opacity: shown ? 1 : 0,
				transition: "opacity 150ms ease-in",
				pointerEvents: shown ? "auto" : "none",
			}}
		>
			<style>{"@keyframes planGateSpin { to { transform: rotate(360deg); } }"}</style>
			{/* The whole point of this component is telling someone their click registered, and a
			    bare spinner tells a screen reader nothing at all. `role="status"` on the wrapper
			    announces the label once, politely; the disc itself stays decorative. */}
			<span role="status" style={{ position: "absolute", width: 1, height: 1, overflow: "hidden", clip: "rect(0 0 0 0)", whiteSpace: "nowrap" }}>
				Loading
			</span>
			<div
				aria-hidden
				style={{
					width: "2.5rem",
					height: "2.5rem",
					borderRadius: "50%",
					border: "3px solid rgba(255, 255, 255, 0.25)",
					borderLeftColor: "#fff",
					animation: "planGateSpin 0.8s linear infinite",
				}}
			/>
		</div>
	);
}

/**
 * What the visitor gets when the boundary above catches: the page they were on, still there, plus
 * one honest sentence and a link.
 *
 * NOT an automatic redirect. The first version of this navigated straight to `/register`, on the
 * reasoning that it is what these CTAs did before the modal existed. Robin caught it: 123yb691qz1
 * retired exactly that navigation — "any CTA aimed at /register or /login now opens the auth modal
 * in place instead of navigating" — and `Navigation.tsx:98` runs the topbar's "Join for free" and
 * "Login" through this same component, so an automatic redirect would have silently teleported
 * people off *every* marketing page, not just the guide pages this was written for. The one
 * navigation that survives that rule is `/login` on a phone (123yb691bdn), which is not this.
 *
 * So the link is offered, not taken. `/register` is still a real page, and a full document load is
 * the one thing that reliably escapes this failure — the whole failure mode is the marketing
 * bundle not supplying contexts the app bundle supplies properly — but choosing to leave stays
 * with the visitor.
 *
 * Inline styles and a plain `<a>` for the same reason `PlanGateLoading` has them: this renders
 * precisely when the tree below has failed, so it must depend on nothing that tree needed. No
 * router, no store, no snackbar, no stylesheet the marketing bundle might not have loaded.
 */
function PlanGateFallback({ onDismiss }: { onDismiss?: () => void }) {
	return (
		<div
			role="alertdialog"
			aria-label="Sign-up could not be opened"
			style={{
				position: "fixed",
				top: 0,
				left: 0,
				right: 0,
				bottom: 0,
				background: "rgba(0, 0, 0, 0.7)",
				zIndex: 9999,
				display: "flex",
				alignItems: "center",
				justifyContent: "center",
				padding: "1rem",
			}}
		>
			<div style={{ background: "#fff", borderRadius: 16, padding: "1.75rem", maxWidth: 360, textAlign: "center", fontFamily: "inherit" }}>
				<p style={{ margin: "0 0 1.25rem", fontSize: 15, lineHeight: 1.5, color: "#1d2b32" }}>
					Something went wrong opening sign-up. You can create your account on its own page instead.
				</p>
				<a
					href="/register"
					style={{
						display: "inline-block",
						padding: "0.7rem 1.4rem",
						borderRadius: 999,
						background: "#4ecf9a",
						color: "#08313a",
						fontWeight: 700,
						textDecoration: "none",
					}}
				>
					Create a free account
				</a>
				{onDismiss && (
					<button
						type="button"
						onClick={onDismiss}
						style={{
							display: "block",
							margin: "0.9rem auto 0",
							background: "none",
							border: "none",
							color: "#5b6b73",
							fontSize: 14,
							cursor: "pointer",
							textDecoration: "underline",
						}}
					>
						Back to the page
					</button>
				)}
			</div>
		</div>
	);
}

/**
 * Contains a crash in the tree below to the gate itself, instead of letting it take the whole
 * page with it — see "When the trap catches anyway" in the header for why this exists: the same
 * failure shape (a provider mounts, a descendant reads null context) has now taken production
 * down twice, as two different contexts (`123yb692k2p`'s snackbar, `123yb692mta`'s store), and
 * nothing else in the marketing tree catches a render error before it reaches the page root.
 *
 * Renders `PlanGateFallback` below — an in-place message with a link — and navigates nowhere by
 * itself. See that component's comment for why the first version of this, which redirected to
 * /register automatically, was wrong.
 *
 * Reports via `console.error` rather than `Sentry.captureException`, unlike `components/
 * ErrorBoundary.tsx`'s app-wide boundary. That one is only ever mounted where `src/index.tsx` has
 * already run `Sentry.init(...)` — the tenant SPA entry. This file is mounted by
 * `entry-client-marketing.tsx`, which never calls `Sentry.init`; `captureException` there would
 * compile, run, and silently report nothing. A `[PlanGate]`-prefixed `console.error` is honest
 * about where this failure is actually visible today — the browser console on a real visitor's
 * machine, and Jam if one happened to be recording — rather than implying a monitoring hook that
 * is not wired up on this bundle.
 */
class PlanGateErrorBoundary extends React.Component<{ children: React.ReactNode; onDismiss?: () => void }, { hasCrashed: boolean }> {
	state: { hasCrashed: boolean } = { hasCrashed: false };

	static getDerivedStateFromError() {
		return { hasCrashed: true };
	}

	componentDidCatch(error: Error, errorInfo: React.ErrorInfo) {
		console.error("[PlanGate] the modal's provider tree threw", error, errorInfo.componentStack);
	}

	render() {
		return this.state.hasCrashed ? <PlanGateFallback onDismiss={this.props.onDismiss} /> : this.props.children;
	}
}

/**
 * The modal half. Renders nothing until the visitor asks for it, so the lazy chunks are not
 * fetched on a page view that never clicks — once clicked, `PlanGateLoading` covers the gap until
 * they are, and `PlanGateErrorBoundary` covers what happens if one of the providers underneath
 * throws instead of settling.
 */
export function PlanGateModal({
	gate,
	title,
	// The homepage and the country hub's route map pass this: it keeps the modal reachable on a
	// phone there. The hub's other CTAs send a phone to the store instead, so they never need it.
	allowMobile = false,
	// Defaults reproduce the plan-with-AI copy/flow every existing caller relies on; the
	// stay-search widget overrides them for its own prompt.
	loginTitle = "Log in to plan with AI",
	initialView = "register",
	// Run after a successful login/registration, INSTEAD of the default below — the modal always
	// closes either way. 123yb692rq2's second symptom: signing in through the gate used to just
	// close the modal and leave the visitor on the marketing page they started on, still looking
	// at "Login" / "Join for free", because nothing sent them anywhere. Every caller that doesn't
	// pass its own `onLogin` now gets `navigateToApp()` (→ `/home`) instead. The stay-search
	// widget (`TravelWidget.tsx`) is the one caller that opts out, passing its own `onLogin` to
	// resume a pending navigation rather than leaving the marketing page.
	onLogin,
}: {
	gate: PlanGate;
	title: string;
	allowMobile?: boolean;
	loginTitle?: string;
	initialView?: "register" | "login";
	onLogin?: () => void;
}) {
	// Delay-then-handoff lifecycle for `PlanGateLoading` — see that component's header comment for
	// why this can't live as a `Suspense` `fallback` prop. `loadingMounted` controls presence in
	// the DOM; `loadingShown` controls the fade-in. On a fast resolve (chunks already cached — the
	// common case), the modal mounts before `appearTimer` fires, so nothing is ever painted for
	// this click. On a slow one, loading fades in at `PLANGATE_LOADING_APPEAR_DELAY_MS` and stays
	// fully opaque until the modal actually commits — no fade-out. It used to cross-fade out while
	// the modal's own overlay faded in from clear, and two half-transparent backdrops mid-transition
	// read as the screen blinking lighter and darkening again, on every first open over a real
	// network. Now `handoff` puts the modal's overlay at the same solid backdrop from its first
	// frame, and `PlanGateModalMounted` removes loading in that same commit — see its comment.
	const [loadingMounted, setLoadingMounted] = useState(false);
	const [loadingShown, setLoadingShown] = useState(false);
	const [handoff, setHandoff] = useState(false);
	const modalMountedRef = useRef(false);

	useEffect(() => {
		if (!gate.authOpen) {
			modalMountedRef.current = false;
			setLoadingMounted(false);
			setLoadingShown(false);
			setHandoff(false);
			return undefined;
		}
		let cancelled = false;

		const appearTimer = setTimeout(() => {
			if (cancelled || modalMountedRef.current) return;
			setHandoff(true);
			setLoadingMounted(true);
			// One frame so the mount paints at opacity 0 before flipping to 1 — otherwise there's
			// nothing for the opacity transition to animate from.
			requestAnimationFrame(() => {
				if (!cancelled && !modalMountedRef.current) setLoadingShown(true);
			});
		}, PLANGATE_LOADING_APPEAR_DELAY_MS);

		return () => {
			cancelled = true;
			clearTimeout(appearTimer);
		};
	}, [gate.authOpen]);

	const onModalMounted = useCallback(() => {
		modalMountedRef.current = true;
		setLoadingMounted(false);
		setLoadingShown(false);
	}, []);

	if (!gate.authOpen) return null;
	return (
		<PlanGateErrorBoundary onDismiss={() => gate.setAuthOpen(false)}>
			<React.Suspense fallback={null}>
				<ReduxProvider>
					<SnackbarProvider>
						<LoginModal
							loginModalIsOpen={gate.authOpen}
							setLoginModalIsOpen={gate.setAuthOpen}
							onLogin={() => {
								gate.setAuthOpen(false);
								if (onLogin) {
									onLogin();
								} else {
									navigateToApp();
								}
							}}
							initialView={initialView}
							{...(allowMobile ? { allowMobile: true } : {})}
							RegisterTitle={title}
							LoginTitle={loginTitle}
							showAppFooter
							panelClassName="auth-modal--make"
							overlayClassName={handoff ? "auth-modal-overlay--instant" : ""}
						/>
						<PlanGateModalMounted onMount={onModalMounted} />
					</SnackbarProvider>
				</ReduxProvider>
			</React.Suspense>
			{loadingMounted && <PlanGateLoading shown={loadingShown} />}
		</PlanGateErrorBoundary>
	);
}
